import classNames from "classnames";
import Checkbox from "components/Checkbox";
import Tag from "components/Tag";
import Tooltip from "components/Tooltip";
import { ConsensusMeterCategory } from "helpers/api";
import useLabels from "hooks/useLabels";
import { useConsensusMeterFilter } from "../hooks/useConsensusMeterFilter";
import { CategoryAggregations, MeterCategories } from "../types";
import { categoryAggregationsToIcons, getCategoriesToColors } from "../utils";

interface CategorySummaryProps {
  category: MeterCategories;
  categoryData: ConsensusMeterCategory | null;
  numResultsAnalyzed: number;
  categoryWins: CategoryAggregations[];
  consensusMeterSuccess: boolean;
  isFilteringEnabled: boolean;
  display?: "grid" | "flex";
  includedSeparator?: boolean;
  threadInteractionId?: string;
}

export const CategorySummary = ({
  category,
  categoryData,
  numResultsAnalyzed,
  categoryWins,
  consensusMeterSuccess,
  isFilteringEnabled,
  display = "flex",
  includedSeparator = false,
  threadInteractionId,
}: CategorySummaryProps) => {
  const { setMeterFilter, meterFilter } =
    useConsensusMeterFilter(threadInteractionId);
  const [categoryLabels, winnerLabels] = useLabels(
    "consensus-meter.categories",
    "consensus-meter.aggregation-winners.icons"
  );

  const paperPercentage =
    numResultsAnalyzed > 0 && categoryData
      ? Math.round(
          (100 * categoryData.paper_count) / numResultsAnalyzed
        ).toString()
      : "0";

  const handleFilterCategory = () => {
    setMeterFilter({ ...meterFilter, [category]: !meterFilter[category] });
  };

  return (
    <div
      className={classNames("items-center h-6", {
        "flex ": display === "flex",
        "[&:not(:last-child)::after]:content-['·'] after:block after:px-3 after:text-fg-muted after:sm-normal":
          display === "flex" && includedSeparator,
        contents: display === "grid",
      })}
      data-testid={`${category}-summary`}
    >
      {isFilteringEnabled ? (
        <Checkbox
          isChecked={meterFilter[category]}
          onToggle={handleFilterCategory}
          checkboxSize="sm"
          className={classNames({
            "col-start-1": display === "grid",
          })}
        />
      ) : (
        <div
          className={classNames("w-4 h-4 flex items-center justify-center", {
            "col-start-1": display === "grid",
          })}
        >
          <div
            style={{
              backgroundColor: getCategoriesToColors()[category],
            }}
            className={"rounded-sm w-3 h-3"}
          />
        </div>
      )}

      <p
        className={classNames("text-fg-base sm-md col-start-2 pl-2", {
          "col-start-2": display === "grid",
        })}
      >
        {categoryLabels[category]}
      </p>
      {consensusMeterSuccess && (
        <p
          className={classNames(
            "text-fg-muted sm-mono-semibold px-6 text-right",
            {
              "pl-[6px] pr-0": display === "flex",
              "col-start-3": display === "grid",
            }
          )}
        >
          {paperPercentage}%
        </p>
      )}
      {categoryWins.length !== 0 && (
        <div
          className={classNames({
            "pl-2": display === "flex",
            "col-start-4": display === "grid",
          })}
        >
          <Tag
            label={categoryWins.map((win) => (
              <Tooltip content={winnerLabels[win]} key={`${category}-${win}`}>
                <i
                  data-testid={`${win}`}
                  className={classNames(
                    "bold-icon text-xs text-fg-muted",
                    categoryAggregationsToIcons[win]
                  )}
                />
              </Tooltip>
            ))}
            size="sm"
          />
        </div>
      )}
    </div>
  );
};
